import { fieldContainsValue } from "shared/utils/helpers";
import {
  buildHeaders,
  successResponse,
  getHeader,
  toOurRequestShape,
  fromOurRequestShape,
  logoutOnAuthError,
  trackApiErrorToSentry,
  runRefreshAndRetry,
} from "./utils";
import { errorHandler } from "./errorHandler";
import { MAX_401_RETRIES, SENTRY_PAYLOAD_MAX_LENGTH } from "./constants";
import { countMetric, captureApiEvent } from "shared/utils/sentryHelpers";

const requestInterceptor = (config) => {
  const { url, headers = {}, body, method, props = {} } = config;

  const baseHeaders = buildHeaders({ ...config, method });

  const requestId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const finalHeaders = {
    ...baseHeaders,
    "X-Request-ID": requestId,
    ...headers,
  };

  if (body instanceof FormData) {
    delete finalHeaders["Content-Type"];
    delete finalHeaders["content-type"];
  }

  if (fieldContainsValue(props?.lat) && fieldContainsValue(props?.long)) {
    finalHeaders["x-lat"] = props.lat;
    finalHeaders["x-long"] = props.long;
  }

  let finalUrl = url;
  if (fieldContainsValue(props?.version)) {
    const baseUrl = process.env.REACT_APP_SERVER_URL || "";
    try {
      if (baseUrl && url.includes("v1")) {
        finalUrl = url.replace(baseUrl, baseUrl.replace("v1", props.version));
      }
    } catch {
      // Fallback to original url if version replace fails
    }
  }

  return {
    ...config,
    url: finalUrl,
    headers: finalHeaders,
  };
};

const responseInterceptor = async (response, options = {}) => {
  const { apiUrl, apiMethod, startTime } = options;
  const isSentryOn = process.env.REACT_APP_SENTRY === "true";
  const duration = startTime ? performance.now() - startTime : null;

  const status = response.status;
  const ok = status >= 200 && status < 300;

  if (ok) {
    if (isSentryOn && duration !== null) {
      countMetric("total_success_api_calls", 1);

      const contentType = getHeader(response, "content-type");
      const isJsonResponse = contentType.includes("application/json");
      const eventData = isJsonResponse && response.data != null ? response.data : { contentType };
      let responsePayload;
      try {
        responsePayload = JSON.stringify(eventData, null, 2);
      } catch {
        responsePayload = "[unserializable payload]";
      }
      if (responsePayload.length > SENTRY_PAYLOAD_MAX_LENGTH) {
        responsePayload =
          responsePayload.slice(0, SENTRY_PAYLOAD_MAX_LENGTH) +
          `… [truncated, total ${responsePayload.length} chars]`;
      }

      captureApiEvent({
        eventId:
          typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        message: `${apiMethod || "API"} ${apiUrl || "request"}`,
        timestamp: new Date().toISOString(),
        tags: {
          event_type: "api_request",
          api_url: apiUrl || "unknown",
          api_method: apiMethod || "unknown",
          api_status: status || "unknown",
          api_duration_ms: duration,
        },
        extra: {
          response: responsePayload,
        },
      });
    }
    const json = (options.config ?? {}).json !== false;
    return json ? successResponse(response) : response;
  }

  // 401 only when response exists; network errors (no response) go to errorInterceptor, no refresh attempt.
  if (status === 401) {
    const { url, config: requestConfig = {}, runTransport } = options;
    const _retryCount = options._retryCount ?? 0;
    const opts = { ...options, duration };

    const reject401 = async () =>
      Promise.reject(
        await errorInterceptor({ response: { status: 401, data: response.data } }, opts)
      );

    if (_retryCount >= MAX_401_RETRIES) {
      return reject401();
    }

    const tokenType = requestConfig.tokenType ?? "access";
    if (tokenType === "access" && typeof runTransport === "function") {
      return runRefreshAndRetry(url, requestConfig, runTransport, options).catch(async (err) => {
        if (err?.success === false) return Promise.reject(err);
        const formatted = await errorInterceptor(err, opts);
        return Promise.reject(formatted);
      });
    }

    return reject401();
  }

  const errFormatted = await errorInterceptor(
    { response: { status, data: response.data } },
    { ...options, duration }
  );
  return Promise.reject(errFormatted);
};

const errorInterceptor = async (error, options = {}) => {
  const { apiUrl, apiMethod, startTime, duration: durationFromCaller } = options;
  const duration = durationFromCaller ?? (startTime != null ? performance.now() - startTime : null);
  const opts = { apiUrl, apiMethod, duration };
  const formatted = errorHandler(error, opts);
  if (error?.__refreshFailure) formatted.sessionExpired = true;
  await trackApiErrorToSentry(error, formatted, opts);
  return logoutOnAuthError(formatted);
};

function attachInterceptors(axiosInstance, config = {}) {
  const onRequest = config.onRequest ?? requestInterceptor;
  const onResponse = config.onResponse ?? responseInterceptor;
  const onError = config.onError ?? errorInterceptor;

  axiosInstance.interceptors.request.use((axiosConfig) => {
    const ourShape = toOurRequestShape(axiosConfig);
    const result = onRequest(ourShape);
    return fromOurRequestShape(result, axiosConfig);
  });

  axiosInstance.interceptors.response.use(
    async (response) => {
      const meta = response.config?.meta ?? {};
      return onResponse(response, meta);
    },
    async (error) => {
      const meta = error.config?.meta ?? {};
      const formatted = await onError(error, meta);
      return Promise.reject(formatted);
    }
  );

  return axiosInstance;
}

export { attachInterceptors };
